import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import WaveSurfer from 'wavesurfer.js';
import StudioToolsDrawer from '../components/StudioToolsDrawer';
import MixerPanel from '../components/music/MixerPanel';
import VideoWorkflowPanel from '../components/music/VideoWorkflowPanel';
import { VolumeControl, getStoredVolume } from '../components/VolumeControls';
import { 
  Sparkles, Loader2, Download, Wand2, Play, Pause, Upload,
  ChevronDown, ChevronUp, Lightbulb, Music, Layers,
  Volume2, RefreshCw, Check, Disc3,
  TrendingUp, Podcast, Film, Dumbbell, Building2, Moon,
  Scissors, RotateCcw, Repeat, Clock, Settings2,
  SkipBack, SkipForward, ZoomIn, ZoomOut, Save,
  Trash2, ExternalLink, Bug, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Music inspiration examples - creator-focused
const INSPIRATION_EXAMPLES = [
  {
    prompt: "Create an upbeat background track for a motivational fitness reel",
    category: "Social Media BGM",
    icon: Dumbbell,
    color: "from-orange-500 to-red-600",
    mood: "upbeat",
    genre: "electronic"
  },
  {
    prompt: "Epic cinematic trailer score with building tension and orchestral elements",
    category: "Cinematic Trailer",
    icon: Film,
    color: "from-purple-500 to-indigo-600",
    mood: "dramatic",
    genre: "cinematic"
  },
  {
    prompt: "Calm meditation ambience with soft piano and nature sounds for relaxation",
    category: "Meditation Ambience",
    icon: Moon,
    color: "from-blue-500 to-cyan-600",
    mood: "calm",
    genre: "ambient"
  },
  {
    prompt: "Professional corporate background music for business presentations",
    category: "Corporate BGM",
    icon: Building2,
    color: "from-emerald-500 to-teal-600",
    mood: "inspiring",
    genre: "pop"
  },
  {
    prompt: "Catchy podcast intro theme with modern electronic vibes, 10 seconds",
    category: "Podcast Intro",
    icon: Podcast,
    color: "from-pink-500 to-rose-600",
    mood: "energetic",
    genre: "electronic"
  },
  {
    prompt: "Dark trap beat with heavy bass and atmospheric synths for content creators",
    category: "Dark Trap Beat",
    icon: TrendingUp,
    color: "from-zinc-600 to-zinc-800",
    mood: "dark",
    genre: "hiphop"
  }
];

// Friendly generation messages
const GENERATION_MESSAGES = [
  "Composing melody...",
  "Building rhythm...",
  "Layering instruments...",
  "Mixing harmonies...",
  "Finalizing your track..."
];

// Mood/Style options — fetched from backend /api/music/presets at runtime
const STYLE_COLORS = {
  lofi: 'from-amber-600 to-orange-700',
  cinematic: 'from-purple-500 to-indigo-500',
  corporate: 'from-emerald-500 to-teal-500',
  edm: 'from-violet-500 to-purple-600',
  ambient: 'from-blue-500 to-cyan-500',
  trap: 'from-zinc-600 to-zinc-800',
  tech: 'from-sky-500 to-blue-600',
  motivational: 'from-orange-500 to-amber-500',
};

const MOOD_COLORS = {
  calm: 'from-blue-400 to-cyan-400',
  energetic: 'from-orange-500 to-red-500',
  inspirational: 'from-amber-400 to-yellow-500',
  dark: 'from-zinc-700 to-zinc-900',
  happy: 'from-green-400 to-emerald-500',
  aggressive: 'from-red-600 to-rose-700',
  chill: 'from-teal-400 to-cyan-500',
};

const MusicStudioPage = () => {
  const { token, user } = useAuth();

  // State
  const [activeTab, setActiveTab] = useState('create');
  const [prompt, setPrompt] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);
  
  // Music settings (advanced)
  const [mood, setMood] = useState('calm');
  const [selectedStyle, setSelectedStyle] = useState('ambient');
  const [duration, setDuration] = useState(30);
  const [energy, setEnergy] = useState(0.5);
  const [presets, setPresets] = useState(null);
  const [previewGenerating, setPreviewGenerating] = useState(false);
  
  // Track management
  const [generatedTracks, setGeneratedTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  
  // Waveform editor state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [volume, setVolume] = useState(() => Math.round(getStoredVolume('audio') * 100));
  const [isLooping, setIsLooping] = useState(false);
  const [waveformReady, setWaveformReady] = useState(false);
  const [waveformZoom, setWaveformZoom] = useState(1);
  const [waveformError, setWaveformError] = useState(null);
  const [waveformLoading, setWaveformLoading] = useState(false);
  const [useFallbackPlayer, setUseFallbackPlayer] = useState(false);
  
  // Debug panel state (founder-only)
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [audioDebugInfo, setAudioDebugInfo] = useState(null);
  
  // Quick tools state
  const [showQuickTools, setShowQuickTools] = useState(true);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [fadeIn, setFadeIn] = useState(0);
  const [fadeOut, setFadeOut] = useState(0);
  const [volumeAdjust, setVolumeAdjust] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs mirroring state used inside long-lived wavesurfer event handlers (avoids stale closures)
  const isLoopingRef = useRef(isLooping);
  const trimStartRef = useRef(trimStart);
  const volumeRef = useRef(volume);
  useEffect(() => { isLoopingRef.current = isLooping; }, [isLooping]);
  useEffect(() => { trimStartRef.current = trimStart; }, [trimStart]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  
  // Refs
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const containerRef = useRef(null);
  const blobUrlRef = useRef(null);
  const trackIdRef = useRef(null);
  const fallbackAudioRef = useRef(null);
  const [waveformContainerReady, setWaveformContainerReady] = useState(false);
  
  // Callback ref for waveform container - triggers when DOM is ready
  const waveformContainerRef = useCallback((node) => {
    waveformRef.current = node;
    setWaveformContainerReady(!!node);
  }, []);

  // Fetch generated tracks
  const fetchGeneratedTracks = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/music/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedTracks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch music:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchGeneratedTracks();
  }, [fetchGeneratedTracks]);

  // Fetch music presets from backend config
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const resp = await axios.get(`${API}/music/presets`);
        setPresets(resp.data);
        // Set defaults from first available
        if (resp.data.styles) {
          const firstStyle = Object.keys(resp.data.styles)[0];
          if (firstStyle) {
            setSelectedStyle(firstStyle);
            setEnergy(resp.data.styles[firstStyle].energy || 0.5);
          }
        }
        if (resp.data.moods?.length) setMood(resp.data.moods[0]);
        if (resp.data.durations?.length) setDuration(resp.data.durations[1] || resp.data.durations[0]);
      } catch (err) {
        console.error('Failed to fetch presets:', err);
      }
    };
    fetchPresets();
  }, []);

  // Cycle through generation messages
  useEffect(() => {
    if (generating) {
      const interval = setInterval(() => {
        setCurrentMessage(prev => (prev + 1) % GENERATION_MESSAGES.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [generating]);

  // Simulate progress
  useEffect(() => {
    if (generating) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 12;
        });
      }, 1200);
      return () => clearInterval(interval);
    }
  }, [generating]);

  // Initialize WaveSurfer when track is selected
  useEffect(() => {
    // Store current track ID to detect race conditions
    const currentTrackId = selectedTrack?.id;
    trackIdRef.current = currentTrackId;
    
    // Cleanup function
    const cleanup = () => {
      // Destroy WaveSurfer instance
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.destroy();
        } catch (e) {
          console.warn('WaveSurfer cleanup error:', e);
        }
        wavesurferRef.current = null;
      }
      
      // Revoke blob URL to prevent memory leak
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    
    // Reset state
    setWaveformReady(false);
    setWaveformError(null);
    setWaveformLoading(false);
    setCurrentTime(0);
    setUseFallbackPlayer(false);
    
    // Check prerequisites
    if (!selectedTrack || selectedTrack.status !== 'ready' || !waveformRef.current) {
      cleanup();
      return;
    }
    
    // Determine audio URL
    let audioUrl = selectedTrack.audio_url;
    if (!audioUrl) {
      setWaveformError('No audio URL available');
      return;
    }
    
    // Handle relative URLs
    if (audioUrl.startsWith('/api')) {
      audioUrl = `${BACKEND_URL}${audioUrl}`;
    }
    
    // Add cache buster to ensure fresh load
    const cacheBuster = `${audioUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    const finalUrl = audioUrl + cacheBuster;
    
    setWaveformLoading(true);
    
    // Clean up any existing instance before creating new one
    cleanup();
    
    // Small delay to ensure DOM is ready and previous instance is destroyed
    const initTimeout = setTimeout(() => {
      // Check if track still matches
      if (trackIdRef.current !== currentTrackId) {
        return;
      }
      
      if (!waveformRef.current) {
        setWaveformError('Waveform container not ready');
        setWaveformLoading(false);
        return;
      }
      
      try {
        // Create WaveSurfer instance (v7 API)
        // backend=MediaElement forces HTML5 Audio instead of WebAudio
        // This is more reliable on mobile and avoids AudioContext resume issues
        const wavesurfer = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: '#6b7280',
          progressColor: '#f97316',
          cursorColor: '#f97316',
          barWidth: 2,
          barGap: 1,
          barRadius: 2,
          height: 100,
          normalize: true,
          interact: true,
          dragToSeek: true,
          hideScrollbar: true,
          autoCenter: true,
          backend: 'MediaElement', // Use HTML5 audio — works on mobile
          url: finalUrl,
        });
        
        // Set up timeout for loading (10 seconds before fallback)
        const loadTimeout = setTimeout(() => {
          if (trackIdRef.current === currentTrackId) {
            console.error('WaveSurfer load timeout — switching to fallback player');
            setWaveformError('Audio waveform took too long to load.');
            setWaveformLoading(false);
            setUseFallbackPlayer(true);
          }
        }, 10000);
        
        // Event handlers
        wavesurfer.on('ready', () => {
          clearTimeout(loadTimeout);
          
          // Final check that track is still valid
          if (trackIdRef.current !== currentTrackId) {
            wavesurfer.destroy();
            return;
          }
          
          setWaveformReady(true);
          setWaveformLoading(false);
          setWaveformError(null);
          setAudioDuration(wavesurfer.getDuration());
          setTrimEnd(wavesurfer.getDuration());
          wavesurfer.setVolume(volumeRef.current / 100);
        });
        
        wavesurfer.on('decode', () => {
          // Audio decoded successfully
        });
        
        wavesurfer.on('error', (err) => {
          clearTimeout(loadTimeout);
          console.error('WaveSurfer error:', err);
          
          if (trackIdRef.current === currentTrackId) {
            setWaveformError(`Failed to load waveform: ${err?.message || 'Unknown error'}`);
            setWaveformLoading(false);
            setUseFallbackPlayer(true);
          }
        });
        
        wavesurfer.on('audioprocess', (time) => {
          setCurrentTime(time);
        });
        
        wavesurfer.on('timeupdate', (time) => {
          setCurrentTime(time);
        });
        
        wavesurfer.on('seeking', (time) => {
          setCurrentTime(time);
        });
        
        wavesurfer.on('play', () => setIsPlaying(true));
        wavesurfer.on('pause', () => setIsPlaying(false));
        wavesurfer.on('finish', () => {
          setIsPlaying(false);
          if (isLoopingRef.current && wavesurfer) {
            const seekPos = trimStartRef.current / (wavesurfer.getDuration() || 1);
            wavesurfer.seekTo(Math.max(0, Math.min(1, seekPos)));
            wavesurfer.play();
          }
        });
        
        // Store reference
        wavesurferRef.current = wavesurfer;
        
      } catch (err) {
        console.error('WaveSurfer initialization error:', err);
        setWaveformError(`Failed to initialize waveform: ${err.message}`);
        setWaveformLoading(false);
      }
    }, 100); // Small delay for DOM stability
    
    // Cleanup on unmount or track change
    return () => {
      clearTimeout(initTimeout);
      cleanup();
    };
    // Granular deps intentional: re-init wavesurfer only when the track identity/source changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrack?.id, selectedTrack?.status, selectedTrack?.audio_url, activeTab, waveformContainerReady]);

  // Retry waveform loading
  const retryWaveform = useCallback(() => {
    if (selectedTrack) {
      // Force re-trigger by updating a dependency
      setSelectedTrack({ ...selectedTrack, _retryTs: Date.now() });
    }
  }, [selectedTrack]);

  // Update volume when slider changes
  useEffect(() => {
    if (wavesurferRef.current && waveformReady) {
      wavesurferRef.current.setVolume(volume / 100);
    }
  }, [volume, waveformReady]);

  // Handle zoom
  useEffect(() => {
    if (wavesurferRef.current && waveformReady) {
      wavesurferRef.current.zoom(waveformZoom * 50);
    }
  }, [waveformZoom, waveformReady]);

  // Handle inspiration click
  const handleInspirationClick = (example) => {
    setPrompt(example.prompt);
    if (example.mood) setMood(example.mood);
    toast.success('Prompt loaded! Customize it or generate now.');
  };

  // Generate music
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe the music you want to create');
      return;
    }

    setGenerating(true);
    setProgress(0);

    try {
      const response = await axios.post(
        `${API}/music/generate`,
        {
          prompt: prompt.trim(),
          duration: parseInt(duration),
          genre: selectedStyle,
          mood: mood,
          style: selectedStyle,
          energy: energy,
          use_enhanced: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newTrack = response.data;
      toast.success('Music generation started!');
      
      // Poll for completion
      pollMusicStatus(newTrack.id);
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate music');
      setGenerating(false);
    }
  };

  const pollMusicStatus = async (musicId) => {
    const maxAttempts = 60;
    let attempts = 0;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setGenerating(false);
        toast.info('Music is still processing. Check the Library for updates.');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/music/${musicId}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const updatedTrack = response.data;
        setProgress(Math.min(90, (attempts / maxAttempts) * 100 + 10));
        
        if (updatedTrack.status === 'ready') {
          setProgress(100);
          setGenerating(false);
          setSelectedTrack(updatedTrack);
          toast.success('Your track is ready!');
          fetchGeneratedTracks();
          setActiveTab('editor');
          return;
        } else if (updatedTrack.status === 'failed') {
          setGenerating(false);
          if (updatedTrack.error === 'silent_audio_detected') {
            toast.error('Generated audio was silent. Credits have been refunded.');
          } else {
            toast.error(updatedTrack.toast_message || 'Music generation failed');
          }
          return;
        }
        
        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        attempts++;
        setTimeout(poll, 3000);
      }
    };
    
    poll();
  };

  // Playback controls — work with both WaveSurfer and fallback audio
  const togglePlayback = async () => {
    if (useFallbackPlayer && fallbackAudioRef.current) {
      if (fallbackAudioRef.current.paused) {
        try {
          fallbackAudioRef.current.muted = false;
          fallbackAudioRef.current.volume = volume / 100;
          await fallbackAudioRef.current.play();
        } catch (e) {
          console.error('Fallback play error:', e);
          toast.error('Playback blocked — tap again');
        }
        setIsPlaying(!fallbackAudioRef.current.paused);
      } else {
        fallbackAudioRef.current.pause();
        setIsPlaying(false);
      }
    } else if (wavesurferRef.current) {
      // Resume AudioContext if suspended (mobile requirement)
      const ctx = wavesurferRef.current.getMediaElement?.()?.audioContext 
                || wavesurferRef.current.backend?.ac;
      if (ctx && ctx.state === 'suspended') {
        try { await ctx.resume(); } catch {}
      }
      wavesurferRef.current.playPause();
    }
  };

  const skipBack = () => {
    if (useFallbackPlayer && fallbackAudioRef.current) {
      fallbackAudioRef.current.currentTime = Math.max(0, fallbackAudioRef.current.currentTime - 5);
    } else if (wavesurferRef.current) {
      const newTime = Math.max(0, currentTime - 5);
      wavesurferRef.current.seekTo(newTime / audioDuration);
    }
  };

  const skipForward = () => {
    if (useFallbackPlayer && fallbackAudioRef.current) {
      fallbackAudioRef.current.currentTime = Math.min(fallbackAudioRef.current.duration, fallbackAudioRef.current.currentTime + 5);
    } else if (wavesurferRef.current) {
      const newTime = Math.min(audioDuration, currentTime + 5);
      wavesurferRef.current.seekTo(newTime / audioDuration);
    }
  };

  // Process audio
  const handleProcessAudio = async () => {
    if (!selectedTrack) return;
    
    setIsProcessing(true);
    
    try {
      const response = await axios.post(
        `${API}/music/${selectedTrack.id}/process`,
        {
          music_id: selectedTrack.id,
          trim_start: trimStart > 0 ? trimStart : null,
          trim_end: trimEnd < audioDuration ? trimEnd : null,
          fade_in: fadeIn,
          fade_out: fadeOut,
          volume_db: volumeAdjust,
          loop_duration: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Audio processed successfully!');
        setSelectedTrack(response.data.processed);
        fetchGeneratedTracks();
      }
    } catch (error) {
      console.error('Processing failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to process audio');
    } finally {
      setIsProcessing(false);
    }
  };

  // Export audio
  const handleExport = async (quality = 'high') => {
    if (!selectedTrack) return;
    
    try {
      toast.loading('Preparing download...', { id: 'export' });
      
      const response = await axios.get(
        `${API}/music/${selectedTrack.id}/export?quality=${quality}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `delphi-music-${selectedTrack.id.slice(0, 8)}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started!', { id: 'export' });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export audio', { id: 'export' });
    }
  };

  // Delete a music track
  const handleDelete = async (trackId, e) => {
    if (e) e.stopPropagation();
    if (!trackId) return;
    
    try {
      toast.loading('Deleting...', { id: 'delete' });
      await axios.delete(`${API}/music/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state (optimistic)
      setGeneratedTracks(prev => prev.filter(t => t.id !== trackId));
      if (selectedTrack?.id === trackId) {
        setSelectedTrack(null);
        setActiveTab('library');
      }
      toast.success('Track deleted', { id: 'delete' });
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete', { id: 'delete' });
      fetchGeneratedTracks(); // revert on failure
    }
  };

  // Open audio in new tab for direct download/play test
  const handleOpenInNewTab = () => {
    if (!selectedTrack?.audio_url) return;
    const url = selectedTrack.audio_url.startsWith('/api')
      ? `${BACKEND_URL}${selectedTrack.audio_url}`
      : selectedTrack.audio_url;
    window.open(url, '_blank');
  };

  // Fetch debug info for selected track (founder-only)
  const fetchAudioDebugInfo = useCallback(async () => {
    if (!selectedTrack?.id || !user?.is_founder) return;
    try {
      const audioUrl = selectedTrack.audio_url?.startsWith('/api')
        ? `${BACKEND_URL}${selectedTrack.audio_url}`
        : selectedTrack.audio_url;
      
      // Fetch headers via HEAD-like request
      let headerInfo = {};
      try {
        const headResp = await fetch(audioUrl, { method: 'GET', headers: { Range: 'bytes=0-0' } });
        headerInfo = {
          status: headResp.status,
          contentType: headResp.headers.get('content-type'),
          contentLength: headResp.headers.get('content-length'),
          acceptRanges: headResp.headers.get('accept-ranges'),
          isPartial: headResp.status === 206,
        };
      } catch (e) {
        headerInfo = { error: e.message };
      }

      // Fetch backend diagnostics
      let backendDiag = null;
      try {
        const resp = await axios.get(`${API}/music/${selectedTrack.id}/diagnostics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        backendDiag = resp.data;
      } catch (e) {
        backendDiag = { error: e.response?.data?.detail || e.message };
      }

      setAudioDebugInfo({
        audioUrl,
        headers: headerInfo,
        backend: backendDiag,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      setAudioDebugInfo({ error: e.message });
    }
  }, [selectedTrack?.id, selectedTrack?.audio_url, token, user?.is_founder]);

  // Handle upload
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 100MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));

    try {
      toast.loading('Uploading audio...', { id: 'audio-upload' });
      await axios.post(`${API}/uploads/audio`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Audio uploaded!', { id: 'audio-upload' });
      fetchGeneratedTracks();
      setActiveTab('library');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed', { id: 'audio-upload' });
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Quick Preview (credit-free, 5-8s sample)
  const handleQuickPreview = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe the music you want to preview');
      return;
    }
    setPreviewGenerating(true);
    try {
      const response = await axios.post(
        `${API}/music/generate`,
        {
          prompt: prompt.trim(),
          duration: 7,
          genre: selectedStyle,
          mood: mood,
          style: selectedStyle,
          energy: energy,
          is_preview: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preview generating... (no credits used)');
      pollMusicStatus(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewGenerating(false);
    }
  };

  // Remix a track (generate variation with same params)
  const handleRemix = async (trackId) => {
    try {
      toast.loading('Starting remix...', { id: 'remix' });
      const response = await axios.post(
        `${API}/music/${trackId}/remix`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Remix generating!', { id: 'remix' });
      pollMusicStatus(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Remix failed', { id: 'remix' });
    }
  };

  // Extend a track (double duration, append to composition)
  const handleExtend = async (trackId) => {
    try {
      toast.loading('Extending track...', { id: 'extend' });
      const response = await axios.post(
        `${API}/music/${trackId}/extend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Extended version generating!', { id: 'extend' });
      pollMusicStatus(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Extend failed', { id: 'extend' });
    }
  };

  // Reset edits
  const resetEdits = () => {
    setTrimStart(0);
    setTrimEnd(audioDuration);
    setFadeIn(0);
    setFadeOut(0);
    setVolumeAdjust(0);
  };

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 pb-20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Music Engine</p>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight text-white">
              AI Music Studio
            </h1>
            <p className="text-zinc-500 text-base max-w-2xl">
              Generate royalty-free custom soundtracks instantly.
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="bg-zinc-900/80 border border-zinc-800 p-1 rounded-full">
                <TabsTrigger 
                  value="create" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="music-tab-create"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create
                </TabsTrigger>
                <TabsTrigger 
                  value="editor" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="music-tab-editor"
                  disabled={!selectedTrack}
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Editor
                </TabsTrigger>
                <TabsTrigger 
                  value="upload" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="music-tab-upload"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </TabsTrigger>
                <TabsTrigger 
                  value="library" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="music-tab-library"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Library
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Create Tab - Prompt First Experience */}
            <TabsContent value="create" className="space-y-8 animate-fade-in-up">
              {/* Main Prompt Area */}
              <div className="max-w-3xl mx-auto">
                {/* Prompt Input */}
                <div className="relative mb-6">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-purple-500/10 to-primary/20 rounded-3xl blur-xl opacity-60" />
                  <div className="relative">
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Create an upbeat background track for a motivational fitness reel..."
                      className="prompt-input w-full px-6 py-5 text-lg"
                      disabled={generating}
                      rows={4}
                      data-testid="music-prompt-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          handleGenerate();
                        }
                      }}
                    />
                    <div className="absolute bottom-4 right-4 text-xs text-zinc-500 hidden sm:block">
                      Cmd/Ctrl + Enter to generate
                    </div>
                  </div>
                </div>

                {/* Generate Button + Quick Preview */}
                <div className="flex flex-col items-center gap-4 mb-8">
                  <div className="flex gap-3">
                    <Button
                      onClick={handleGenerate}
                      disabled={!prompt.trim() || generating}
                      className={`btn-generate min-w-[200px] ${generating ? 'btn-generating' : ''}`}
                      data-testid="generate-music-btn"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {GENERATION_MESSAGES[currentMessage]}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 mr-2" />
                          Generate Music
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleQuickPreview}
                      disabled={!prompt.trim() || generating || previewGenerating}
                      className="border-primary/30 text-primary hover:bg-primary/10"
                      data-testid="quick-preview-btn"
                    >
                      {previewGenerating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Quick Preview
                      <span className="ml-1 text-[10px] opacity-60">FREE</span>
                    </Button>
                  </div>

                  {/* Progress Bar */}
                  {generating && (
                    <div className="w-full max-w-md space-y-2 animate-fade-in-up">
                      <div className="progress-creative">
                        <div 
                          className="progress-creative-bar" 
                          style={{ width: `${Math.max(10, progress)}%` }}
                        />
                      </div>
                      <p className="text-center text-sm text-zinc-500">
                        {progress > 0 ? `${Math.round(progress)}% complete` : 'Starting...'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Customize Audio Panel — Config Driven */}
                <div className="collapsible-panel mb-8">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="collapsible-trigger"
                    data-testid="customize-audio-toggle"
                  >
                    <span className="flex items-center gap-2">
                      <Settings2 className="w-4 h-4" />
                      Customize Audio
                    </span>
                    {showAdvanced ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {showAdvanced && presets && (
                    <div className="collapsible-content animate-fade-in-up space-y-6">
                      {/* Style Presets */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400">Style</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {Object.entries(presets.styles || {}).map(([key, cfg]) => (
                            <button
                              key={key}
                              onClick={() => {
                                setSelectedStyle(key);
                                setEnergy(cfg.energy || 0.5);
                              }}
                              className={`p-3 rounded-xl border text-center transition-all ${
                                selectedStyle === key
                                  ? 'bg-primary/10 border-primary text-white'
                                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                              }`}
                              data-testid={`style-preset-${key}`}
                            >
                              <div className={`w-6 h-6 mx-auto mb-2 rounded-full bg-gradient-to-br ${STYLE_COLORS[key] || 'from-zinc-500 to-zinc-600'}`} />
                              <p className="font-medium text-sm">{cfg.label}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Mood Presets */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400">Mood</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {(presets.moods || []).map((m) => (
                            <button
                              key={m}
                              onClick={() => setMood(m)}
                              className={`p-3 rounded-xl border text-center transition-all capitalize ${
                                mood === m
                                  ? 'bg-primary/10 border-primary text-white'
                                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                              }`}
                              data-testid={`mood-preset-${m}`}
                            >
                              <div className={`w-6 h-6 mx-auto mb-2 rounded-full bg-gradient-to-br ${MOOD_COLORS[m] || 'from-zinc-500 to-zinc-600'}`} />
                              <p className="font-medium text-sm">{m}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400">Duration</Label>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {(presets.durations || []).map((d) => (
                            <button
                              key={d}
                              onClick={() => setDuration(d)}
                              className={`p-3 rounded-xl border text-center transition-all ${
                                duration === d
                                  ? 'bg-primary/10 border-primary text-white'
                                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                              }`}
                              data-testid={`duration-preset-${d}`}
                            >
                              <p className="font-medium text-sm">{d}s</p>
                              <p className="text-xs opacity-60">{d <= 15 ? 'Intro' : d <= 30 ? 'Short' : d <= 60 ? 'Standard' : d <= 90 ? 'Extended' : 'Full'}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Energy Slider */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400 flex items-center justify-between">
                          <span>Energy Level</span>
                          <span className="text-xs text-zinc-500">{Math.round(energy * 100)}%</span>
                        </Label>
                        <Slider
                          value={[energy * 100]}
                          onValueChange={(v) => setEnergy(v[0] / 100)}
                          max={100}
                          step={5}
                          data-testid="energy-slider"
                        />
                        <div className="flex justify-between text-[10px] text-zinc-600">
                          <span>Calm</span>
                          <span>Moderate</span>
                          <span>Intense</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Inspiration Section */}
              {!generating && (
                <div className="space-y-6">
                  <div className="section-divider">
                    <span className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Get Inspired
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {INSPIRATION_EXAMPLES.map((example, index) => {
                      const Icon = example.icon;
                      return (
                        <button
                          key={example.prompt}
                          onClick={() => handleInspirationClick(example)}
                          className="group relative bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 text-left hover:border-zinc-700 hover:bg-zinc-800/60 transition-all hover:scale-[1.02] active:scale-[0.98]"
                          data-testid={`inspiration-card-${index}`}
                        >
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${example.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-1">
                            {example.category}
                          </p>
                          <p className="text-sm text-zinc-300 line-clamp-2 group-hover:text-white transition-colors">
                            {example.prompt.slice(0, 55)}...
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Editor Tab - Waveform Editor */}
            <TabsContent value="editor" className="space-y-6 animate-fade-in-up">
              {selectedTrack ? (
                <div className="max-w-4xl mx-auto space-y-6">
                  {/* Track Info Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/30 to-purple-500/30 flex items-center justify-center">
                        <Disc3 className={`w-7 h-7 text-primary ${isPlaying ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold truncate max-w-md">
                          {selectedTrack.prompt?.slice(0, 50) || 'Untitled Track'}...
                        </h3>
                        <p className="text-sm text-zinc-500">
                          {formatTime(audioDuration)} - {selectedTrack.mood || 'Music'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedTrack(null);
                        setActiveTab('library');
                      }}
                    >
                      Change Track
                    </Button>
                  </div>

                  {/* Waveform Container */}
                  <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 space-y-4">
                    {/* Waveform — hidden when using fallback */}
                    <div 
                      ref={waveformContainerRef} 
                      className={`w-full min-h-[100px] rounded-lg overflow-hidden bg-zinc-800/50 ${
                        waveformError || useFallbackPlayer ? 'hidden' : ''
                      }`}
                      data-testid="waveform-container"
                    />
                    
                    {/* Loading State */}
                    {waveformLoading && !waveformReady && !waveformError && !useFallbackPlayer && (
                      <div className="flex items-center justify-center h-[100px] bg-zinc-800/50 rounded-lg">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        <span className="ml-2 text-zinc-500">Loading waveform...</span>
                      </div>
                    )}
                    
                    {/* Hard Fallback Player — auto-shown when WaveSurfer fails */}
                    {(useFallbackPlayer || waveformError) && selectedTrack?.audio_url && (
                      <div className="flex flex-col items-center justify-center p-5 bg-zinc-800/30 rounded-xl border border-zinc-700 space-y-3" data-testid="fallback-player-container">
                        <div className="flex items-center gap-2 text-sm text-zinc-400 font-medium">
                          <Volume2 className="w-4 h-4" />
                          Audio Player
                        </div>
                        <audio 
                          ref={fallbackAudioRef}
                          controls 
                          src={selectedTrack.audio_url?.startsWith('/api') 
                            ? `${BACKEND_URL}${selectedTrack.audio_url}` 
                            : selectedTrack.audio_url}
                          className="w-full max-w-lg"
                          data-testid="fallback-audio-player"
                          preload="auto"
                          onLoadedMetadata={(e) => {
                            setAudioDuration(e.target.duration);
                            setTrimEnd(e.target.duration);
                          }}
                          onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                          onPlay={() => setIsPlaying(true)}
                          onPause={() => setIsPlaying(false)}
                          onEnded={() => setIsPlaying(false)}
                          onError={(e) => console.error('Fallback audio error:', e.target.error)}
                        >
                          Your browser does not support audio playback.
                        </audio>
                        {waveformError && (
                          <div className="flex items-center gap-2">
                            <p className="text-[11px] text-zinc-600">{waveformError}</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={retryWaveform}
                              className="text-xs h-7"
                              data-testid="retry-waveform-btn"
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Retry Waveform
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Time Display */}
                    <div className="flex justify-between text-sm text-zinc-500">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(audioDuration)}</span>
                    </div>

                    {/* Playback Controls */}
                    <div className="flex items-center justify-center gap-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={skipBack}
                        disabled={!waveformReady && !useFallbackPlayer}
                      >
                        <SkipBack className="w-5 h-5" />
                      </Button>
                      <Button 
                        size="lg" 
                        className="rounded-full w-14 h-14 bg-primary hover:bg-primary/90"
                        onClick={togglePlayback}
                        disabled={!waveformReady && !useFallbackPlayer}
                        data-testid="play-pause-btn"
                      >
                        {isPlaying ? (
                          <Pause className="w-6 h-6" />
                        ) : (
                          <Play className="w-6 h-6 ml-1" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={skipForward}
                        disabled={!waveformReady && !useFallbackPlayer}
                      >
                        <SkipForward className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Secondary Controls */}
                    <div className="flex items-center justify-between">
                      {/* Volume */}
                      <div className="flex items-center gap-3 w-48">
                        <Volume2 className="w-4 h-4 text-zinc-500" />
                        <Slider
                          value={[volume]}
                          onValueChange={(v) => { 
                            setVolume(v[0]);
                            try { localStorage.setItem('playback_volume_audio', (v[0] / 100).toString()); } catch {}
                          }}
                          max={100}
                          step={1}
                          className="flex-1"
                          aria-label="Audio volume"
                        />
                        <span className="text-xs text-zinc-500 w-8">{volume}%</span>
                      </div>

                      {/* Loop & Zoom */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant={isLooping ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setIsLooping(!isLooping)}
                          className={isLooping ? "bg-primary/20 text-primary" : ""}
                        >
                          <Repeat className="w-4 h-4 mr-1" />
                          Loop
                        </Button>
                        <div className="flex items-center gap-1 border border-zinc-700 rounded-lg px-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setWaveformZoom(Math.max(0.5, waveformZoom - 0.5))}
                            className="h-8 w-8 p-0"
                          >
                            <ZoomOut className="w-4 h-4" />
                          </Button>
                          <span className="text-xs text-zinc-500 w-8 text-center">{waveformZoom}x</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setWaveformZoom(Math.min(5, waveformZoom + 0.5))}
                            className="h-8 w-8 p-0"
                          >
                            <ZoomIn className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Tools Panel */}
                  <div className="collapsible-panel">
                    <button
                      onClick={() => setShowQuickTools(!showQuickTools)}
                      className="collapsible-trigger"
                      data-testid="quick-tools-toggle"
                    >
                      <span className="flex items-center gap-2">
                        <Scissors className="w-4 h-4" />
                        Quick Tools
                      </span>
                      {showQuickTools ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    
                    {showQuickTools && (
                      <div className="collapsible-content animate-fade-in-up space-y-6">
                        {/* Trim */}
                        <div className="space-y-3">
                          <Label className="text-zinc-400 flex items-center gap-2">
                            <Scissors className="w-4 h-4" />
                            Trim
                          </Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-zinc-500">Start: {formatTime(trimStart)}</Label>
                              <Slider
                                value={[trimStart]}
                                onValueChange={(v) => setTrimStart(Math.min(v[0], trimEnd - 0.1))}
                                max={audioDuration}
                                step={0.1}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">End: {formatTime(trimEnd)}</Label>
                              <Slider
                                value={[trimEnd]}
                                onValueChange={(v) => setTrimEnd(Math.max(v[0], trimStart + 0.1))}
                                max={audioDuration}
                                step={0.1}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Fade In/Out */}
                        <div className="space-y-3">
                          <Label className="text-zinc-400 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Fade
                          </Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-zinc-500">Fade In: {fadeIn.toFixed(1)}s</Label>
                              <Slider
                                value={[fadeIn]}
                                onValueChange={(v) => setFadeIn(v[0])}
                                max={5}
                                step={0.1}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">Fade Out: {fadeOut.toFixed(1)}s</Label>
                              <Slider
                                value={[fadeOut]}
                                onValueChange={(v) => setFadeOut(v[0])}
                                max={5}
                                step={0.1}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Volume Adjust */}
                        <div className="space-y-3">
                          <Label className="text-zinc-400 flex items-center gap-2">
                            <Volume2 className="w-4 h-4" />
                            Volume Adjust: {volumeAdjust > 0 ? '+' : ''}{volumeAdjust} dB
                          </Label>
                          <Slider
                            value={[volumeAdjust]}
                            onValueChange={(v) => setVolumeAdjust(v[0])}
                            min={-12}
                            max={12}
                            step={1}
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4 border-t border-zinc-800">
                          <Button
                            onClick={handleProcessAudio}
                            disabled={isProcessing}
                            className="flex-1"
                            data-testid="apply-edits-btn"
                          >
                            {isProcessing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                Apply Edits
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={resetEdits}
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Reset
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Studio Tools Drawer - Phase B */}
                  <StudioToolsDrawer 
                    musicId={selectedTrack?.id}
                    onProcessed={(newTrack) => {
                      setSelectedTrack(newTrack);
                      toast.success('Processing applied!');
                      fetchGeneratedTracks();
                    }}
                    disabled={!waveformReady}
                  />
                  
                  {/* Multi-Track Mixer - Phase C */}
                  <MixerPanel
                    musicId={selectedTrack?.id}
                    onMixComplete={(mixTrack) => {
                      setSelectedTrack(mixTrack);
                      toast.success('Mix exported successfully!');
                      fetchGeneratedTracks();
                    }}
                    disabled={!waveformReady}
                  />
                  
                  {/* Phase D: Video Workflow Panel */}
                  <VideoWorkflowPanel
                    musicId={selectedTrack?.id}
                    onProcessComplete={(result) => {
                      toast.success('Audio processed!');
                      fetchGeneratedTracks();
                    }}
                    disabled={!waveformReady}
                  />

                  {/* Export / Download / Delete Section */}
                  <div className="flex gap-3 flex-wrap">
                    <Button
                      onClick={() => handleExport('high')}
                      className="flex-1 btn-oracle"
                      data-testid="export-mp3-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export MP3
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleOpenInNewTab}
                      data-testid="download-open-btn"
                      title="Open audio file in new tab for direct playback test"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Audio
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleRemix(selectedTrack.id)}
                      data-testid="remix-track-editor-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Remix
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleExtend(selectedTrack.id)}
                      data-testid="extend-track-editor-btn"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Extend
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-400 hover:text-red-300 hover:border-red-500/50"
                      onClick={(e) => handleDelete(selectedTrack.id, e)}
                      data-testid="delete-track-editor-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>

                  {/* Debug Panel — founder-only */}
                  {user?.is_founder && (
                    <div className="mt-4">
                      <button
                        onClick={() => { setShowDebugPanel(!showDebugPanel); if (!audioDebugInfo) fetchAudioDebugInfo(); }}
                        className="flex items-center gap-2 text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
                        data-testid="debug-panel-toggle"
                      >
                        <Bug className="w-3 h-3" />
                        {showDebugPanel ? 'Hide' : 'Show'} Audio Debug
                      </button>
                      {showDebugPanel && (
                        <div className="mt-2 p-4 bg-zinc-900/80 border border-zinc-800 rounded-xl font-mono text-xs space-y-2" data-testid="debug-panel">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-zinc-500">Audio Diagnostics</span>
                            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={fetchAudioDebugInfo}>
                              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                            </Button>
                          </div>
                          {audioDebugInfo ? (
                            <>
                              <p><span className="text-zinc-500">URL:</span> <span className="text-zinc-300 break-all">{audioDebugInfo.audioUrl}</span></p>
                              <p><span className="text-zinc-500">HTTP Status:</span> <span className={audioDebugInfo.headers?.status === 206 || audioDebugInfo.headers?.status === 200 ? 'text-green-400' : 'text-red-400'}>{audioDebugInfo.headers?.status}</span></p>
                              <p><span className="text-zinc-500">Content-Type:</span> <span className="text-zinc-300">{audioDebugInfo.headers?.contentType}</span></p>
                              <p><span className="text-zinc-500">Content-Length:</span> <span className="text-zinc-300">{audioDebugInfo.headers?.contentLength}</span></p>
                              <p><span className="text-zinc-500">Accept-Ranges:</span> <span className="text-zinc-300">{audioDebugInfo.headers?.acceptRanges}</span></p>
                              <p><span className="text-zinc-500">206 Partial:</span> <span className={audioDebugInfo.headers?.isPartial ? 'text-green-400' : 'text-zinc-400'}>{String(audioDebugInfo.headers?.isPartial)}</span></p>
                              {audioDebugInfo.backend && !audioDebugInfo.backend.error && (
                                <>
                                  <hr className="border-zinc-800 my-2" />
                                  <p><span className="text-zinc-500">File Exists:</span> <span className={audioDebugInfo.backend.file_exists ? 'text-green-400' : 'text-red-400'}>{String(audioDebugInfo.backend.file_exists)}</span></p>
                                  <p><span className="text-zinc-500">Size:</span> <span className="text-zinc-300">{audioDebugInfo.backend.file_size_bytes} bytes</span></p>
                                  <p><span className="text-zinc-500">Format:</span> <span className="text-zinc-300">{audioDebugInfo.backend.format} / {audioDebugInfo.backend.codec}</span></p>
                                  <p><span className="text-zinc-500">Duration:</span> <span className="text-zinc-300">{audioDebugInfo.backend.duration_seconds}s</span></p>
                                  <p><span className="text-zinc-500">Bitrate:</span> <span className="text-zinc-300">{audioDebugInfo.backend.bitrate} bps</span></p>
                                  <p><span className="text-zinc-500">Sample Rate:</span> <span className="text-zinc-300">{audioDebugInfo.backend.sample_rate} Hz / {audioDebugInfo.backend.channels}ch</span></p>
                                  <p><span className="text-zinc-500">Mean Vol:</span> <span className={audioDebugInfo.backend.mean_volume_db > -45 ? 'text-green-400' : 'text-red-400'}>{audioDebugInfo.backend.mean_volume_db} dB</span></p>
                                  <p><span className="text-zinc-500">Max Vol:</span> <span className={audioDebugInfo.backend.max_volume_db > -35 ? 'text-green-400' : 'text-red-400'}>{audioDebugInfo.backend.max_volume_db} dB</span></p>
                                  <p><span className="text-zinc-500">Silent:</span> <span className={audioDebugInfo.backend.is_silent ? 'text-red-400' : 'text-green-400'}>{String(audioDebugInfo.backend.is_silent)}</span></p>
                                  <p><span className="text-zinc-500">Header Hex:</span> <span className="text-zinc-300">{audioDebugInfo.backend.header_hex}</span></p>
                                </>
                              )}
                              {audioDebugInfo.backend?.error && (
                                <p className="text-red-400">Backend error: {audioDebugInfo.backend.error}</p>
                              )}
                            </>
                          ) : (
                            <p className="text-zinc-500">Loading...</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Music className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-lg font-semibold mb-2">No track selected</h3>
                  <p className="text-zinc-500 mb-6">Generate or select a track to edit</p>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => setActiveTab('create')} className="btn-oracle">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create New
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab('library')}>
                      <Layers className="w-4 h-4 mr-2" />
                      Browse Library
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="animate-fade-in-up">
              <div className="max-w-2xl mx-auto">
                <div className="border-2 border-dashed border-zinc-700 rounded-3xl p-12 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept="audio/mpeg,audio/wav,audio/aac,audio/ogg,audio/flac,audio/mp4,.mp3,.wav,.aac,.ogg,.flac,.m4a"
                    onChange={handleUpload}
                    className="hidden"
                    id="audio-upload"
                    data-testid="audio-upload-input"
                  />
                  <label htmlFor="audio-upload" className="cursor-pointer block">
                    <div className="w-20 h-20 mx-auto mb-6 bg-zinc-800 rounded-2xl flex items-center justify-center">
                      <Upload className="w-10 h-10 text-zinc-500" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Upload Your Audio</h3>
                    <p className="text-zinc-500 mb-6">
                      Drag and drop or click to browse
                    </p>
                    <p className="text-xs text-zinc-600">
                      Supports MP3, WAV, AAC, OGG, FLAC, M4A - Max 100MB
                    </p>
                  </label>
                </div>
              </div>
            </TabsContent>

            {/* Library Tab */}
            <TabsContent value="library" className="animate-fade-in-up">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Your Music Library</h2>
                  <Button variant="outline" size="sm" onClick={fetchGeneratedTracks}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {generatedTracks.length === 0 ? (
                  <div className="text-center py-16 border border-zinc-800 rounded-2xl">
                    <Music className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
                    <h3 className="text-lg font-semibold mb-2">No tracks yet</h3>
                    <p className="text-zinc-500 mb-6">Create your first track to see it here</p>
                    <Button onClick={() => setActiveTab('create')} className="btn-oracle">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create Your First Track
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {generatedTracks.map((track) => (
                      <div
                        key={track.id}
                        className={`bg-zinc-900/60 border rounded-xl p-4 cursor-pointer transition-all hover:border-zinc-700 ${
                          selectedTrack?.id === track.id ? 'border-primary ring-2 ring-primary/20' : 'border-zinc-800'
                        }`}
                        onClick={() => {
                          setSelectedTrack(track);
                          if (track.status === 'ready') {
                            setActiveTab('editor');
                          }
                        }}
                        data-testid={`track-item-${track.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/30 to-purple-500/30 flex items-center justify-center flex-shrink-0">
                            {track.status === 'generating' ? (
                              <Loader2 className="w-7 h-7 text-primary animate-spin" />
                            ) : (
                              <Music className="w-7 h-7 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{track.prompt || 'Untitled Track'}</p>
                            {/* Metadata tags */}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {track.style && (
                                <span className={`inline-flex items-center px-2 py-0.5 text-[10px] rounded-full border border-zinc-700 bg-gradient-to-r ${STYLE_COLORS[track.style] || 'from-zinc-600 to-zinc-700'} bg-clip-text text-transparent font-semibold`}>
                                  {track.style}
                                </span>
                              )}
                              {track.mood && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 capitalize">
                                  {track.mood}
                                </span>
                              )}
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">
                                {track.duration || '30'}s
                              </span>
                              {track.is_preview && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                                  Preview
                                </span>
                              )}
                              {track.energy != null && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500">
                                  E:{Math.round(track.energy * 100)}%
                                </span>
                              )}
                            </div>
                            {/* Waveform visual preview bar */}
                            {track.status === 'ready' && (
                              <div className="flex items-center gap-0.5 mt-2 h-4 opacity-60">
                                {Array.from({ length: 32 }, (_, i) => (
                                  <div
                                    key={i}
                                    className="w-1 rounded-full bg-primary/40"
                                    style={{ height: `${4 + Math.abs(Math.sin((i + (track.id?.charCodeAt(0) || 0)) * 0.5)) * 12}px` }}
                                  />
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {track.status === 'ready' ? (
                                <span className="status-badge status-ready">
                                  <Check className="w-3 h-3" />
                                  Ready
                                </span>
                              ) : track.status === 'generating' ? (
                                <span className="status-badge status-generating">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Generating
                                </span>
                              ) : track.status === 'failed' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                                  <AlertTriangle className="w-3 h-3" />
                                  {track.error === 'silent_audio_detected' ? 'Silent (Refunded)' : 'Failed'}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Remix button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={track.status !== 'ready'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemix(track.id);
                              }}
                              title="Remix (create variation)"
                              data-testid={`remix-track-${track.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            {/* Extend button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={track.status !== 'ready'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExtend(track.id);
                              }}
                              title="Extend duration"
                              data-testid={`extend-track-${track.id}`}
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={track.status !== 'ready'}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrack(track);
                                setActiveTab('editor');
                              }}
                              title="Edit"
                            >
                              <Wand2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={track.status !== 'ready'}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrack(track);
                                handleExport('high');
                              }}
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-zinc-500 hover:text-red-400"
                              onClick={(e) => handleDelete(track.id, e)}
                              data-testid={`delete-track-${track.id}`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
    </AppShell>
  );
};

export default MusicStudioPage;
